import { AxiosError } from "axios";

import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import { UNIVERSITY_ID } from "@/constants/admin";
import adminAxios from "@/lib/axios";

/**
 * Admin CRUD responses use `{ success: true, data: T }` (see server APIResponse).
 * Normalize so callers receive `T` directly (matches lead drop form API helpers).
 */
function unwrapLeadDropAdminBody<T>(body: unknown): T | null {
  if (body == null) return null;
  if (typeof body !== "object") return body as T;
  const o = body as { success?: boolean; data?: T };
  if (o.success === true && "data" in o) return (o.data ?? null) as T | null;
  return body as T;
}

export interface ILeadDropPopup {
  id?: number;
  university_settings_id?: number;
  form_title?: string;
  form_title_color?: string;
  form_sub_text?: string;
  form_sub_text_color?: string;
  checkbox_description?: string;
  checkbox_description_color?: string;
  checkbox_color?: string;
  /** Optional consent links referenced by {{N:Label}} tokens in `checkbox_description` (slot 1 = terms, slot 2 = privacy). */
  terms_conditions_link?: string;
  privacy_policy_link?: string;
  button_color?: string;
  button_text_color?: string;
  disabled_button_color?: string;
  validation_color?: string;
  signup_button_text?: string;
  signup_icon?: string;
  otp_title_text?: string;
  otp_title_color?: string;
  edit_text_color?: string;
  resend_text_color?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  bg_image_alt?: string;
  existing_user_desktop_image?: string;
  existing_user_mobile_image?: string;
  existing_user_image_alt?: string;
  existing_user_title?: string;
  existing_user_description?: string;
  existing_user_title_color?: string;
  existing_user_description_color?: string;
  brochure_desktop_image?: string;
  brochure_mobile_image?: string;
  brochure_image_alt?: string;
  brochure_title?: string;
  brochure_description?: string;
  brochure_title_color?: string;
  brochure_description_color?: string;
  brochure_link_color?: string;
  new_user_desktop_image?: string;
  new_user_mobile_image?: string;
  new_user_image_alt?: string;
  new_user_title?: string;
  new_user_description?: string;
  new_user_title_color?: string;
  new_user_description_color?: string;
  popup_display_interval_1?: string;
  popup_display_interval_2?: string;
  popup_display_interval_3?: string;
  enable_delay_form?: number;
  enable_exit_form?: number;
  status?: number;
  is_deleted?: number;
  created_by?: number | null;
  updated_by?: number | null;
  createdAt?: string;
  updatedAt?: string;
  variant?: string;
  is_active?: number;
  title_2?: string;
  end_date?: string;
  end_time?: string;
  otp_title?: string;
  close_icon?: string;
  description?: string;
  gradient_end?: string;
  title_2_color?: string;
  close_icon_alt?: string;
  download_title?: string;
  gradient_start?: string;
  date_text_color?: string;
  otp_header_text?: string;
  resend_otp_text?: string;
  edit_number_text?: string;
  date_number_color?: string;
  description_color?: string;
  download_subtitle?: string;
  new_user_subtitle?: string;
  button_step_1_text?: string;
  button_step_2_text?: string;
  button_step_3_text?: string;
  brochure_button_icon?: string;
  brochure_button_text?: string;
  download_description?: string;
  download_title_color?: string;
  new_user_button_icon?: string;
  new_user_button_text?: string;
  otp_description_text?: string;
  date_background_color?: string;
  otp_header_text_color?: string;
  resend_otp_text_color?: string;
  edit_number_text_color?: string;
  existing_user_subtitle?: string;
  otp_header_description?: string;
  button_background_color?: string;
  download_subtitle_color?: string;
  new_user_subtitle_color?: string;
  button_step_1_text_color?: string;
  button_step_2_text_color?: string;
  button_step_3_text_color?: string;
  download_background_image?: string;
  existing_user_button_icon?: string;
  existing_user_button_text?: string;
  new_user_background_image?: string;
  brochure_button_text_color?: string;
  download_description_color?: string;
  new_user_button_text_color?: string;
  otp_description_text_color?: string;
  existing_user_subtitle_color?: string;
  otp_header_description_color?: string;
  download_background_image_alt?: string;
  new_user_background_image_alt?: string;
  button_step_2_background_color?: string;
  button_step_3_background_color?: string;
  existing_user_background_image?: string;
  existing_user_button_text_color?: string;
  brochure_button_background_color?: string;
  new_user_button_background_color?: string;
  existing_user_background_image_alt?: string;
  existing_user_button_background_color?: string;
  /** Lead popup 7 — values typically live in additional_fields */
  dynamic_field_definitions?: string;
  get_otp_button_text?: string;
  /** Lead popup 7 — OTP step primary label (defaults in form constants). */
  verify_otp_button_text?: string;
  get_otp_enabled_color?: string;
  get_otp_disabled_color?: string;
  phone_row_background_color?: string;
  /** Variant 6 segmented intake success / returning (often in additional_fields) */
  seg3_success_highlight_text?: string;
  seg3_success_highlight_color?: string;
  seg3_success_disclaimer?: string;
  seg3_success_disclaimer_color?: string;
  seg3_success_button_url?: string;
  seg4_subtitle?: string;
  seg4_subtitle_color?: string;
  seg4_button_url?: string;
  seg4_disclaimer?: string;
  seg4_disclaimer_color?: string;
  seg4_highlight_text?: string;
  seg4_highlight_color?: string;
  /** Lead popup/form 8 — post-submit redirect URL configured in CMS (replaces env-derived server value). */
  redirection_url?: string;
  /** Lead popup/form 8 — background color behind the desktop hero image column. */
  desktop_image_bg_color?: string;
  /** Lead popup/form 8 — success CTA label when the course HAS a POK (default "Continue with Your Application"). */
  success_button_text_with_pok?: string;
  /** Lead popup/form 8 — success CTA label when the course has NO POK (default "Close"). */
  success_button_text_without_pok?: string;
  /** Lead popups 1/2/4/8/9 — optional URL for the success/continue button. When set, the button opens it in a new tab; when empty, the button keeps its current behaviour (POK redirect, close, or back-to-website). */
  success_button_link?: string;
  /** JSON object for variant-specific fields (isJsonField: true) */
  additional_fields?: Record<string, unknown>;
}

import { UNIVERSITY_SETTINGS_SECTION_TYPES } from "@/constants/admin";
import {
  getUniversitySettingsBySection,
  upsertUniversitySettings,
} from "./universitySettings";

/**
 * Set lead drop popup variant for university (upsert)
 */
export async function setLeadDropPopupVariant(
  variant: string,
  isActive: boolean = true
) {
  try {
    const response = await upsertUniversitySettings(
      UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_POPUP,
      variant,
      isActive
    );
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get lead drop popup by university and variant
 * Returns null silently for 404 (expected when variant doesn't exist yet)
 */
export async function getLeadDropPopupByVariant(variant: string) {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.LEAD_DROP_POPUP.GET_BY_UNIVERSITY_AND_VARIANT(UNIVERSITY_ID, variant)
    );
    if (response?.status !== 200) return null;
    return unwrapLeadDropAdminBody<ILeadDropPopup>(response.data);
  } catch (error) {
    const axiosError = error as AxiosError;
    // 404 is expected when checking if data exists - return null silently
    if (axiosError.response?.status === 404) {
      return null;
    }
    const errorResponse = handleApiError(axiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Create lead drop popup
 */
export async function createLeadDropPopup(payload: Partial<ILeadDropPopup>) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.LEAD_DROP_POPUP.CREATE,
      {
        ...payload,
        university_id: UNIVERSITY_ID,
      }
    );
    if (response?.status !== 201) return null;
    return unwrapLeadDropAdminBody<ILeadDropPopup>(response.data);
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Update lead drop popup metadata
 */
export async function updateLeadDropPopup(
  id: number,
  payload: Partial<ILeadDropPopup>
) {
  try {
    const response = await adminAxios.put(ADMIN_API_ENDPOINTS.LEAD_DROP_POPUP.UPDATE(id),
      payload
    );
    if (response?.status !== 200) return null;
    return unwrapLeadDropAdminBody<ILeadDropPopup>(response.data);
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get all lead drop popups for university
 */
export async function getAllLeadDropPopups() {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.LEAD_DROP_POPUP.GET_BY_UNIVERSITY_ID(UNIVERSITY_ID)
    );
    if (response?.status !== 200) return null;
    const rows = unwrapLeadDropAdminBody<ILeadDropPopup[]>(response.data);
    return rows ?? [];
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Delete lead drop popup
 */
export async function deleteLeadDropPopup(id: number) {
  try {
    const response = await adminAxios.delete(ADMIN_API_ENDPOINTS.LEAD_DROP_POPUP.UPDATE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get lead drop popup settings by section
 * Returns the active variant and status for lead drop popup
 */
export async function getLeadDropPopup() {
  try {
    const response = await getUniversitySettingsBySection(
      UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_POPUP
    );
    if (response?.data) {
      // Transform to match expected format
      return {
        data: {
          lead_drop_popup_variant: response.data.variant,
          lead_drop_popup_status: response.data.is_active,
          university_settings_id: response.data.id,
        },
      };
    } else if (response) {
      // Handle case where response is the data directly (not wrapped)
      return {
        data: {
          lead_drop_popup_variant: response.variant,
          lead_drop_popup_status: response.is_active,
          university_settings_id: response.id,
        },
      };
    }
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
